import { useMemo } from 'react';
import { useEmployeeSkillGapQuery } from '../../__generated__/graphql';
import { mapSkillGapRows, sortSkillGapRows, SkillGapRow } from './logic';

type Result = {
  rows: SkillGapRow[];
  loading: boolean;
  error: boolean;
};

// Headless data access for the skill gap: query + shaping, so each surface
// (employee Skills tab, performance review sidebar) owns its own presentation.
export const useEmployeeSkillGap = (employeeId: string): Result => {
  const { data, loading, error } = useEmployeeSkillGapQuery({
    variables: { employeeId },
  });

  // An inaccessible employee resolves to `null` without a GraphQL error, so
  // treat it as an empty result rather than data that never arrived.
  const rows = useMemo(() => {
    const employee = data?.manager.employee;
    return employee
      ? sortSkillGapRows(
          mapSkillGapRows(
            employee.positionRequiredSkills,
            employee.employeeSkills,
          ),
        )
      : [];
  }, [data]);

  // Only the genuine first load has no data yet; a resolved-but-empty response
  // is not "still loading".
  return { rows, loading: loading && !data, error: Boolean(error) };
};
